"use client";

import { useCallback, useEffect, useMemo, useState } from "react";

import { InfoIcon } from "lucide-react";

import { ClientInfoLayout } from "@/components/backoffice/cat/vehicle-assignment/client-step/client-info-layout";
import {
  IndividualsSection,
  type EditableIndividualProfile,
} from "@/components/backoffice/cat/vehicle-assignment/client-step/client-individuals-section";
import { RetailIndividualCreateForm } from "./retail-individual-create-form";
import { RetailCompanySection } from "./retail-company-section";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type {
  RetailCompany,
  RetailIndividual,
} from "@/lib/cat/retail-client.types";
import {
  PARTICULIER_SERVER_ERROR_MESSAGE,
  SIRET_VERIFY_INTERNAL_ERROR_MESSAGE,
  isSiretLookupBlocking,
  type ParticulierSearchOutcome,
  type SiretLookupResult,
} from "@/lib/cat/client-step-contracts";
import {
  useCatParticulierSearchMutation,
  useCatSiretVerifyMutation,
} from "@/lib/queries";

type RentAnswer = "no" | "yes";
type DirectorAnswer = "no" | "yes";
type ExclusiveKind = "individual" | "company";

function RetailClientStep({
  onCanProceedChange,
}: {
  onCanProceedChange?: (canProceed: boolean) => void;
}) {
  const [rentSale, setRentSale] = useState<RentAnswer | null>(null);
  const [directorSale, setDirectorSale] = useState<DirectorAnswer | null>(null);
  const [exclusiveKind, setExclusiveKind] = useState<ExclusiveKind | null>(
    null,
  );

  const [siretInput, setSiretInput] = useState("");
  const [siretResult, setSiretResult] = useState<SiretLookupResult | null>(
    null,
  );
  const [validatedCompany, setValidatedCompany] =
    useState<RetailCompany | null>(null);

  const [pEmail, setPEmail] = useState("");
  const [pPhone, setPPhone] = useState("");
  const [pLastName, setPLastName] = useState("");
  const [pFirstName, setPFirstName] = useState("");

  const [particulierOutcome, setParticulierOutcome] =
    useState<ParticulierSearchOutcome | null>(null);
  const [selectedIndividualId, setSelectedIndividualId] = useState<
    string | null
  >(null);
  const [ficheEdit, setFicheEdit] = useState<EditableIndividualProfile | null>(
    null,
  );
  const [individualCreateValid, setIndividualCreateValid] = useState(false);

  const particulierSearchMutation = useCatParticulierSearchMutation();
  const siretVerifyMutation = useCatSiretVerifyMutation();

  const resetClientState = useCallback(() => {
    setSiretInput("");
    setSiretResult(null);
    setValidatedCompany(null);
    setParticulierOutcome(null);
    setSelectedIndividualId(null);
    setFicheEdit(null);
    setIndividualCreateValid(false);
    setPEmail("");
    setPPhone("");
    setPLastName("");
    setPFirstName("");
  }, []);

  const resetSiretSearch = useCallback(() => {
    setSiretInput("");
    setSiretResult(null);
    setValidatedCompany(null);
  }, []);

  const resetParticulierSearch = useCallback(() => {
    setParticulierOutcome(null);
    setSelectedIndividualId(null);
    setFicheEdit(null);
    setPEmail("");
    setPPhone("");
    setPLastName("");
    setPFirstName("");
    setIndividualCreateValid(false);
  }, []);

  const selectedIndividual = useMemo((): RetailIndividual | null => {
    if (!selectedIndividualId || !particulierOutcome) return null;
    if (particulierOutcome.kind !== "list") return null;
    return (
      particulierOutcome.individuals.find(
        (i) => i.id === selectedIndividualId,
      ) ?? null
    );
  }, [particulierOutcome, selectedIndividualId]);

  const handleSelectIndividual = useCallback(
    (id: string | null) => {
      setSelectedIndividualId(id);
      if (!id) {
        setFicheEdit(null);
        return;
      }
      if (particulierOutcome?.kind !== "list") {
        setFicheEdit(null);
        return;
      }
      const individual = particulierOutcome.individuals.find(
        (i) => i.id === id,
      );
      if (!individual) {
        setFicheEdit(null);
        return;
      }
      setFicheEdit({
        addressLine: individual.addressLine,
        city: individual.city,
        email: individual.email,
        phone: individual.phone,
        postalCode: individual.postalCode,
      });
    },
    [particulierOutcome],
  );

  const ficheComplete = Boolean(
    ficheEdit?.email.trim() &&
    ficheEdit.phone.trim() &&
    ficheEdit.addressLine.trim() &&
    ficheEdit.postalCode.trim() &&
    ficheEdit.city.trim(),
  );

  const companyPathComplete = Boolean(
    validatedCompany &&
    siretResult?.kind === "known" &&
    !isSiretLookupBlocking(siretResult),
  );

  const individualPathComplete = useMemo(() => {
    if (!particulierOutcome) return false;
    if (particulierOutcome.kind === "error") return false;
    if (particulierOutcome.kind === "no_internal_collaborator") return false;
    if (particulierOutcome.kind === "no_criteria") return false;
    if (particulierOutcome.kind === "list") {
      if (particulierOutcome.individuals.length === 0) {
        return individualCreateValid;
      }
      if (!selectedIndividualId || !ficheEdit) return false;
      return ficheComplete;
    }
    return false;
  }, [
    ficheComplete,
    ficheEdit,
    individualCreateValid,
    particulierOutcome,
    selectedIndividualId,
  ]);

  const canProceed = useMemo(() => {
    if (rentSale === null) return false;
    if (rentSale === "yes") return true;
    if (directorSale === null) return false;

    if (directorSale === "yes") {
      return companyPathComplete && individualPathComplete;
    }
    if (exclusiveKind === null) return false;
    if (exclusiveKind === "company") return companyPathComplete;

    return individualPathComplete;
  }, [
    companyPathComplete,
    directorSale,
    exclusiveKind,
    individualPathComplete,
    rentSale,
  ]);

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  const handleVerifySiret = useCallback(async () => {
    try {
      const result = await siretVerifyMutation.mutateAsync(siretInput);
      setSiretResult(result);
      if (result.kind === "known") {
        setValidatedCompany(result.company);
      } else {
        setValidatedCompany(null);
      }
    } catch (error) {
      console.error("[RetailClientStep] SIRET verify", error);
      // TODO: pipe to centralized logging
      setValidatedCompany(null);
      setSiretResult({
        kind: "internal_error",
        message: SIRET_VERIFY_INTERNAL_ERROR_MESSAGE,
      });
    }
  }, [siretInput, siretVerifyMutation]);

  const handleSiretInputChange = useCallback((value: string) => {
    setSiretInput(value);
    setSiretResult(null);
    setValidatedCompany(null);
  }, []);

  const handleIndividualSearch = useCallback(async () => {
    setSelectedIndividualId(null);
    setFicheEdit(null);
    try {
      const outcome = await particulierSearchMutation.mutateAsync({
        context: "retail",
        email: pEmail.trim(),
        firstName: pFirstName.trim() || undefined,
        lastName: pLastName.trim(),
        phone: pPhone.trim(),
      });
      setParticulierOutcome(outcome);
    } catch (error) {
      console.error("[RetailClientStep] particulier search", error);
      // TODO: pipe to centralized logging
      setParticulierOutcome({
        kind: "error",
        message: PARTICULIER_SERVER_ERROR_MESSAGE,
      });
    }
  }, [pEmail, pFirstName, pLastName, pPhone, particulierSearchMutation]);

  const showBlock2 = rentSale === "no";
  const showDirector = showBlock2;
  const showExclusive = showBlock2 && directorSale === "no";
  const showCompanyPanel =
    showBlock2 &&
    (directorSale === "yes" ||
      (directorSale === "no" && exclusiveKind === "company"));
  const showIndividualPanel =
    showBlock2 &&
    (directorSale === "yes" ||
      (directorSale === "no" && exclusiveKind === "individual"));

  return (
    <ClientInfoLayout>
      <div className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Contexte vente</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-2">
                <Label className="text-sm font-medium">
                  Est-ce une vente RENT ?
                </Label>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground inline-flex"
                      aria-label="Aide"
                    >
                      <InfoIcon className="size-4" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    Affichage provisoire : sera conditionné par les données
                    Mulesoft (contrat RENT).
                  </TooltipContent>
                </Tooltip>
              </div>
              <RadioGroup
                className="flex flex-wrap gap-4"
                value={rentSale ?? undefined}
                onValueChange={(v) => {
                  setRentSale(v as RentAnswer);
                  setDirectorSale(null);
                  setExclusiveKind(null);
                  resetClientState();
                }}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="rent-yes" value="yes" />
                  <Label htmlFor="rent-yes">Oui</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="rent-no" value="no" />
                  <Label htmlFor="rent-no">Non</Label>
                </div>
              </RadioGroup>
            </div>

            {rentSale === "yes" ? (
              <Alert>
                <AlertTitle>Canal RENT</AlertTitle>
                <AlertDescription>
                  Le SIRET distributeur sera fourni par Mulesoft lors du premier
                  appel de recherche véhicule. Aucune saisie n&apos;est requise
                  ici.
                </AlertDescription>
              </Alert>
            ) : null}

            {showDirector ? (
              <div className="space-y-3">
                <Label className="text-sm font-medium">
                  Est-ce une vente à dirigeant ?
                </Label>
                <RadioGroup
                  className="flex flex-wrap gap-4"
                  value={directorSale ?? undefined}
                  onValueChange={(v) => {
                    setDirectorSale(v as DirectorAnswer);
                    setExclusiveKind(null);
                    resetClientState();
                  }}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="dir-yes" value="yes" />
                    <Label htmlFor="dir-yes">Oui</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="dir-no" value="no" />
                    <Label htmlFor="dir-no">Non</Label>
                  </div>
                </RadioGroup>
              </div>
            ) : null}

            {showExclusive ? (
              <div className="space-y-3">
                <Label className="text-sm font-medium">Type de client</Label>
                <RadioGroup
                  className="flex flex-wrap gap-4"
                  value={exclusiveKind ?? undefined}
                  onValueChange={(v) => {
                    setExclusiveKind(v as ExclusiveKind);
                    resetClientState();
                  }}
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="tk-p" value="individual" />
                    <Label htmlFor="tk-p">Particulier</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem id="tk-s" value="company" />
                    <Label htmlFor="tk-s">Société</Label>
                  </div>
                </RadioGroup>
              </div>
            ) : null}
          </CardContent>
        </Card>

        {showBlock2 ? (
          <>
            <Separator />
            <div className="space-y-6">
              {showCompanyPanel ? (
                <RetailCompanySection
                  isVerifying={siretVerifyMutation.isPending}
                  siretInput={siretInput}
                  siretResult={siretResult}
                  validatedCompany={validatedCompany}
                  onResetSearch={resetSiretSearch}
                  onSiretChange={handleSiretInputChange}
                  onVerify={handleVerifySiret}
                />
              ) : null}

              {showIndividualPanel ? (
                <IndividualsSection
                  idPrefix="retail-p"
                  isSearchLoading={particulierSearchMutation.isPending}
                  sectionTitle="Particulier"
                  criteriaLabel="Critères de recherche"
                  emailPlaceholder="Ex: tom.blanc@demo.retail"
                  searchButtonLabel="Rechercher"
                  resultsLabel={(count) =>
                    `${count} résultat(s) — sélectionnez une fiche`
                  }
                  ficheEdit={ficheEdit}
                  onFicheChange={setFicheEdit}
                  onResetSearch={resetParticulierSearch}
                  onSearch={handleIndividualSearch}
                  onSelectIndividual={handleSelectIndividual}
                  outcome={particulierOutcome}
                  pEmail={pEmail}
                  pFirstName={pFirstName}
                  pLastName={pLastName}
                  pPhone={pPhone}
                  selectedIndividual={selectedIndividual}
                  selectedIndividualId={selectedIndividualId}
                  setPEmail={setPEmail}
                  setPFirstName={setPFirstName}
                  setPLastName={setPLastName}
                  setPPhone={setPPhone}
                  creationConfig={{
                    emptyResultsLabel: "Aucun client trouvé",
                    openCreateLabel: "Créer une nouvelle fiche client",
                    closeCreateLabel: "Fermer la création de fiche",
                    onCreateCanProceedChange: setIndividualCreateValid,
                    renderCreateForm: (onCreateCanProceedChange) => (
                      <RetailIndividualCreateForm
                        onCanProceedChange={onCreateCanProceedChange}
                      />
                    ),
                  }}
                />
              ) : null}
            </div>
          </>
        ) : null}
      </div>
    </ClientInfoLayout>
  );
}

export { RetailClientStep };
